import React, {useCallback, useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import createAxiosInstance from '../support/AxiosSupport';
import RecognitionReviewAction from './chat/RecognitionReviewAction';
import ItemReanalysisDialog from './ItemReanalysisDialog';
import {SparklesIcon} from './icons';
import type {AiRecognitionRequest} from '../utils/aiRecognition';

interface ItemReanalysisPanelProps {
    itemId: string;
    /** Ключи фотографий вещи (источник «текущие фото»). */
    itemPhotos: string[];
    /** Свой гардероб — влияет на выбор судьбы нового фото (FR-7). */
    isOwnWardrobe: boolean;
    /** Карточка обновлена результатом анализа — перезагрузить вещь без перезагрузки страницы. */
    onItemChanged?: () => void;
}

/** Заявка ещё «живая»: её результат относится к этой вещи и ждёт пользователя. */
const isActive = (status?: string): boolean =>
    !!status && !['DONE', 'CANCELED'].includes(status);

/** Параметр входа из кебаб-меню карточки: открывает диалог сразу после перехода на вкладку. */
const OPEN_PARAM = 'reanalyze';

/**
 * Блок «Повторный ИИ-анализ» во вкладке AI карточки вещи (item-reanalysis-dialog, FR-1/FR-4).
 *
 * Держит весь поток на одном экране: запуск с выбором фото, прогресс через очередь статусов,
 * проверку результата и обновление карточки. Раньше кебаб-меню молча создавало заявку и
 * уводило пользователя на общий список распознаваний.
 */
const ItemReanalysisPanel: React.FC<ItemReanalysisPanelProps> = ({
    itemId, itemPhotos, isOwnWardrobe, onItemChanged,
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [requestId, setRequestId] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [lookedUp, setLookedUp] = useState(false);

    // Незавершённая заявка по этой вещи переживает перезагрузку страницы: ищем её по
    // sourceItemId, иначе после F5 прогресс выглядел бы потерянным.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const api = await createAxiosInstance();
                const res = await api.get<AiRecognitionRequest[]>('/ai-recognition');
                if (cancelled) return;
                const active = res.data.find(r => r.sourceItemId === itemId && isActive(r.status));
                setRequestId(active?.id ?? null);
            } catch {
                if (!cancelled) setRequestId(null);
            } finally {
                if (!cancelled) setLookedUp(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [itemId]);

    // Вход из кебаб-меню карточки: ?reanalyze=1. Параметр снимаем сразу, чтобы возврат
    // «назад» и обновление страницы не открывали диалог заново.
    useEffect(() => {
        if (!searchParams.get(OPEN_PARAM)) return;
        setDialogOpen(true);
        const next = new URLSearchParams(searchParams);
        next.delete(OPEN_PARAM);
        setSearchParams(next, {replace: true});
    }, [searchParams, setSearchParams]);

    const handleStarted = useCallback((id: string) => {
        setRequestId(id);
        setLookedUp(true);
    }, []);

    const handleSaved = useCallback(() => {
        setRequestId(null);
        onItemChanged?.();
    }, [onItemChanged]);

    return (
        <div
            data-testid="item-reanalysis-panel"
            className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3"
        >
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <SparklesIcon className="w-4 h-4 text-primary-500"/>
                    Повторный ИИ-анализ
                </h3>
                {lookedUp && !requestId && (
                    <button
                        type="button"
                        onClick={() => setDialogOpen(true)}
                        data-testid="item-reanalysis-start"
                        className="btn-secondary py-1 px-3 text-sm"
                    >
                        Запустить анализ
                    </button>
                )}
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400">
                ИИ пересмотрит характеристики вещи по фотографиям — текущим или новым. Результат
                вы проверите и подтвердите сами; запуск расходует квоту ИИ-запросов.
            </p>

            {requestId && (
                <div data-testid="item-reanalysis-progress">
                    <RecognitionReviewAction
                        requestId={requestId}
                        onSaved={handleSaved}
                        reviewLabel="Проверить и обновить вещь"
                        savedLabel="Карточка обновлена"
                    />
                </div>
            )}

            <ItemReanalysisDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                itemId={itemId}
                itemPhotos={itemPhotos}
                isOwnWardrobe={isOwnWardrobe}
                onStarted={handleStarted}
            />
        </div>
    );
};

export default ItemReanalysisPanel;
